import {
  Button,
  Checkbox,
  Form,
  Input,
  Radio,
  ReactSelect,
} from "@hydra/atom/components";
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  Header,
  FormLeftHeader,
  PhoneNumberSelector,
  Select,
} from "@/components/common";
import appSettings from "@/settings";
import { PrependInput } from "@/components/admin";
import {
  setFormValue,
  setNextStep,
} from "@/reducers/admin/companySettingsReducer.js";
import {
  createCompanySetting,
  updateCompanySettingProfile,
  uploadCompanyLogo
} from "@/api/admin/companySettingApi";
import { getCompanies } from "@/api/finance/companyApi";
import { prepareGeneralData } from "@/utils/admin/helpers";
import {
  validationMessages,
  areaUnitType,
  dateFormat,
} from "@/utils/admin/constants";
import showToast from "@/utils/toast/helpers";
import { selectActiveCompany } from "@/store/appSlice";
import { getDynamicObjectRecords } from "@/api/dynamic/dynamicObjectNameApi";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { setDateFormat } from "@/api/user/authApi";
import { nestedOptionStyles } from "@/utils/select/constants";

function GeneralSettings({ state, dispatch }) {
  const hiddenFileInput = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const [sameAsCompanyAddress, setSameAsCompanyAddress] = useState(false);
  const activeCompany = useSelector(selectActiveCompany);

  const leftContent = (title, subtitle) => (
    <div className="container-header-left-content">
      <div className="title">{title}</div>
      <div className="subtitle">{subtitle}</div>
    </div>
  );

  const saveMutation = useMutation(createCompanySetting, {
    onError: () => {
      showToast("Could not create. Try again!", "error");
    },
    onSuccess: (saveMutationData) => {
      dispatch({
        type: "SET_FORM_VALUE",
        payload: {
          key: "companyId",
          value: saveMutationData.data?.id,
        },
      });
      showToast("Created successfully", "success");
      dispatch(setNextStep(2));
    },
  });

  const updateMutation = useMutation(
    ({ id: recordId, data: recordData }) =>
      updateCompanySettingProfile(recordId, recordData),
    {
      onError: () => {
        showToast("Could not update. Try again!", "error");
      },
      onSuccess: (data) => {
        const company =
          activeCompany?.companySettings?.id === data?.data?.id ?
            activeCompany :
            null;
        if (company) {
          setDateFormat(data?.data?.dateFormat);
        }
        showToast("Updated successfully", "success");
        dispatch(setNextStep(2));
      },
    }
  );

  const handleClick = () => {
    hiddenFileInput.current.click();
  };

  const handleChange = async (event) => {
    const uploadedFile = event.target.files[0];
    try {
      setIsUploading(true);
      const response = await uploadCompanyLogo(uploadedFile, state.companyId);

      if (response) {
        const { url } = response;
        const logoUrl = url.split(`${appSettings.minioBaseUrl}`);
        dispatch(setFormValue("logo", logoUrl[1]));
        showToast("Company logo updated successfully!");
      }
    } catch (error) {
      showToast("Could not upload company logo. Try again!", "error");
    } finally {
      setIsUploading(false);
      event.target.value = null;
    }
  };

  const onSubmit = () => {
    const stepOneFormattedData = prepareGeneralData(state);
    if (state.generalSettingsEditStatus) {
      updateMutation.mutate({
        id: state.companyId,
        data: stepOneFormattedData,
      });
    } else {
      saveMutation.mutate(stepOneFormattedData);
    }
  };

  return (
    <section className="general-setting-body">
      <Form onSubmit={onSubmit} shouldScrollOnError>
        <div className="setting-header">
          <Header
            leftContent={leftContent(
              "General Information",
              "Get emails to find out what’s going on when you’re not online. You can turn them off anytime."
            )}
          />
          <hr className="full-hr" />
        </div>
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Company Logo"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-3">
                {
                  state.logo && !isUploading && (
                    <img
                      src={`${appSettings.minioBaseUrl}${state.logo}`}
                      alt="company logo"
                    />
                  )
                }
              </div>
              <div className="col-md-3">
                <div className="form-action-item">
                  <input
                    type="file"
                    accept={["png", "jpg", "jpeg", "svg"]}
                    ref={hiddenFileInput}
                    onChange={handleChange}
                    style={{ position: "absolute", opacity: "0", cursor: "pointer", zIndex: "-1" }}
                  />
                  <button type="button" className="form-action-text" onClick={handleClick}>
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Company Name"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <PrependInput
                  value={state.companyName}
                  name="Company Name"
                  id="companyName"
                  error={validationMessages.required}
                  onChange={(value) =>
                    dispatch(setFormValue("companyName", value))}
                  prependText="tulierp.com/"
                  rules="required"
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Parent Company"
              subtitle="Select parent group or company of the current company"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <Select
                  id="parent-company"
                  name="parent-company"
                  queryKey="parent-company"
                  lookupObjectName="Company"
                  placeholder="Select parent company"
                  value={state.parent}
                  onChange={(value) => dispatch(setFormValue("parent", value))}
                  optionsLoader={() => getCompanies()}
                  dataAccessKey="data"
                  optionLabelKey="name"
                  optionValueKey="id"
                  hasNestedOptions
                  styles={nestedOptionStyles}
                  isClearable
                  messages={validationMessages.required}
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Organization"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6 report-field select-field ">
                <Select
                  name="organization"
                  queryKey="organization"
                  placeholder="Select organization"
                  value={state?.organization}
                  onChange={(value) =>
                    dispatch(setFormValue("organization", value))}
                  optionsLoader={() =>
                    getDynamicObjectRecords(
                      dynamicObjectMap.get("EntityObjectName")
                    )}
                  dataAccessKey="data"
                  optionLabelKey="name"
                  optionValueKey="id"
                  isClearable
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Company Website"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <PrependInput
                  value={state.companyWebsite}
                  onChange={(value) =>
                    dispatch(setFormValue("companyWebsite", value))}
                  name="Company Website"
                  id="companyWebsite"
                  prependText="https://"
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Company Address"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <Input
                  value={state.companyAddress}
                  onChange={(value) =>
                    dispatch(setFormValue("companyAddress", value))}
                  name="Company Address"
                  id="companyAddress"
                  placeholder="Company Address"
                  type="text"
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Company Postal Address"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <Input
                  value={state.companyPostalAddress}
                  onChange={(value) =>
                    dispatch(setFormValue("companyPostalAddress", value))}
                  name="Company Postal Address"
                  id="companyPostalAddress"
                  placeholder="Company Postal Address"
                  type="text"
                />
              </div>
              <div className="col-md-12">
                <Checkbox
                  className="settings-checkbox"
                  label="Same as Company Address"
                  value={sameAsCompanyAddress}
                  onChange={() => {
                    if (!sameAsCompanyAddress === true) {
                      dispatch(
                        setFormValue(
                          "companyPostalAddress",
                          state.companyAddress
                        )
                      );
                    }
                    setSameAsCompanyAddress(!sameAsCompanyAddress);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Phone Numbers"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <h1 className="form-field-text">Phone Number 01</h1>
                <PhoneNumberSelector
                  id="number-1"
                  name="number-1"
                  value={state.phoneNumbers[0]}
                  onChange={(value) =>
                    dispatch(
                      setFormValue("phoneNumbers", [
                        value,
                        state.phoneNumbers[1],
                      ])
                    )}
                  error={validationMessages.required}
                  rules="required"
                />
              </div>
              <div className="col-md-6">
                <h1 className="form-field-text">Phone Number 02</h1>
                <PhoneNumberSelector
                  id="number-2"
                  name="number-2"
                  value={state.phoneNumbers[1]}
                  onChange={(value) =>
                    dispatch(
                      setFormValue("phoneNumbers", [
                        state.phoneNumbers[0],
                        value,
                      ])
                    )}
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Company General Information Email"
              subtitle="Enter a General Information email of your company"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <Input
                  value={state.email}
                  onChange={(value) => dispatch(setFormValue("email", value))}
                  name="Company General Information Email"
                  id="email"
                  placeholder="you@example.com"
                  type="email"
                  rules="required"
                  messages={validationMessages.required}
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Does your business require multi language support"
              subtitle="Select to specify if segments of information should be added to the standard account codes."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-4">
                <div
                  className={
                    state.multiLanguageSupport ?
                      "form-radio-card-primary" :
                      "card"
                  }
                  role="presentation"
                  onClick={() =>
                    dispatch(setFormValue("multiLanguageSupport", true))}
                >
                  <Radio
                    className="radio-text"
                    label="Yes"
                    itemValue
                    type="radio"
                    value={state.multiLanguageSupport}
                    onChange={() =>
                      dispatch(setFormValue("multiLanguageSupport", true))}
                  >
                    <Radio.CheckIcon>
                      <span className="material-icons">circle</span>
                    </Radio.CheckIcon>
                  </Radio>
                  <h1 className="form-field-radio-text">
                    We need Multi-language support
                  </h1>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className={
                    !state.multiLanguageSupport ?
                      "form-radio-card-primary" :
                      "card"
                  }
                  role="presentation"
                  onClick={() =>
                    dispatch(setFormValue("multiLanguageSupport", false))}
                >
                  <Radio
                    className="radio-text"
                    label="No"
                    itemValue
                    type="radio"
                    value={!state.multiLanguageSupport}
                  >
                    <Radio.CheckIcon>
                      <span className="material-icons">circle</span>
                    </Radio.CheckIcon>
                  </Radio>
                  <h1 className="form-field-radio-text">
                    We don’t need multi-language support
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Unit Type And Date Format"
              subtitle="This will be displayed on your Company profile."
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <ReactSelect
                  name="unitType"
                  placeholder="Select Area Unit"
                  options={areaUnitType}
                  value={state.areaMeasurementUnitType}
                  onChange={(value) =>
                    dispatch(setFormValue("areaMeasurementUnitType", value))}
                />
              </div>
              <div className="col-md-6">
                <ReactSelect
                  name="dateFormat"
                  placeholder="Select Date Format"
                  options={dateFormat}
                  value={state.dateFormat}
                  onChange={(value) =>
                    dispatch(setFormValue("dateFormat", value))}
                />
              </div>
            </div>
          </div>
        </div>
        <hr className="full-hr" />
        <footer className="setting-footer">
          <Button
            className="save-btn"
            type="submit"
            loading={saveMutation.isLoading}
          >
            Save and Move Next
          </Button>
        </footer>
      </Form>
    </section>
  );
}

export default GeneralSettings;
